import React, { useContext, useEffect, useState } from 'react';
import SinglePageForm, { getEmailRules } from '../common/SinglePageForm';
import { Input, Button, Alert, Form } from 'antd';
import { startReset } from '../services/user';
import { useHistory } from 'react-router-dom';
import { APP_CCX, CustomAppContext } from '../common/CustomAppContext';
import './ForgotPasswordPage.less';
import { trackCustomEvent } from '../helpers';

export default ForgotPasswordPage;

/**
 * @name ForgotPasswordPage
 * @desc ForgotPasswordPage component.
 */
function ForgotPasswordPage(props) {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [sent, setSent] = useState(false);
    const [expired, setExpired] = useState(false);
    const [email, setEmail] = useState(false);
    const { currentApp, getCustomAppPath, getCustomAppButtonStyle } =
        useContext(CustomAppContext);

    /**
     *
     * @desc Check if there is an expired prop passed and alter UI relatively.
     *
     */
    useEffect(() => {
        setExpired(!!props.location?.state?.expired);
    }, [props.location?.state?.expired]);

    /**
     * @name handleSubmit
     * @desc Handles form submit.
     * @private
     */
    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            await startReset(values);
            setSent(true);
            setEmail(values.login);
            form.resetFields();
            trackCustomEvent(currentApp, 'forgot_password_success', 'click');
        } catch (err) {
            setError(err.message);
            trackCustomEvent(currentApp, 'forgot_password_failed', 'click');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitFailed = (errorInfo) => {
        console.log('Failed:', errorInfo);
        trackCustomEvent(currentApp, 'forgot_password_error', 'click');
        setError('Please check your input and try again.');
    };

    const handleNotReceivedClick = () => {
        setSent(false);
        trackCustomEvent(
            currentApp,
            'forgot_password_email_not_received',
            'click'
        );
    };

    const history = useHistory();

    const renderSent = () => {
        if (sent) {
            return (
                <SinglePageForm.Footer>
                    <Button
                        data-testid="forgot-password-form-not-received-button"
                        onClick={handleNotReceivedClick}
                        className={`ForgotPasswordPage-form-send-${currentApp}`}
                    >
                        Haven't received the email?
                    </Button>
                </SinglePageForm.Footer>
            );
        }

        return (
            <div>
                <Form.Item
                    label="Email"
                    name="login"
                    rules={getEmailRules()}
                    validateTrigger="onBlur"
                >
                    <Input
                        data-testid="forgot-password-form-input-email"
                        disabled={loading}
                        placeholder="Enter your email"
                    />
                </Form.Item>
                <SinglePageForm.Footer>
                    {error ? <Alert message={error} type="error" /> : null}
                    <br />
                    <div className="ForgotPasswordPage-form-button">
                        <Button
                            data-testid="forgot-password-form-button-back"
                            disabled={loading}
                            htmlType="button"
                            onClick={history.goBack}
                            className={`ForgotPasswordPage-form-back-button-${currentApp}`}
                        >
                            Back
                        </Button>
                        <Button
                            data-testid="forgot-password-form-button-submit"
                            loading={loading}
                            type="primary"
                            htmlType="submit"
                            style={getCustomAppButtonStyle()}
                            className={`ForgotPasswordPage-form-send-${currentApp}`}
                        >
                            Send
                        </Button>
                    </div>
                </SinglePageForm.Footer>
            </div>
        );
    };

    const setTitle = () => {
        if (sent) {
            return 'Check your email';
        } else {
            if (expired) {
                return 'Link has expired';
            } else {
                return 'Forgot your password?';
            }
        }
    };

    const setContent = () => {
        if (sent && email) {
            return (
                <p>
                    We've sent you instructions to <strong>{email}</strong> for
                    resetting your password. If you haven't received an email
                    from us within a couple of minutes, please check your spam
                    folder.
                </p>
            );
        } else {
            if (expired) {
                return (
                    <p>
                        Your reset password link has expired. Enter your email
                        below to get a new link.
                    </p>
                );
            } else {
                return (
                    <p>
                        To reset your password, enter the email address that you
                        used to set up your {currentApp.toUpperCase()} account.
                        We'll send you a link to help you get back into your
                        account.
                    </p>
                );
            }
        }
    };

    return (
        <SinglePageForm
            form={form}
            onFinish={handleSubmit}
            onFinishFailed={handleSubmitFailed}
            data-testid="forgot-password-form"
        >
            <SinglePageForm.Content>
                <h1 className="ForgotPasswordPage-form-title">{setTitle()}</h1>
                {setContent()}

                {sent ? (
                    <p>
                        Still having trouble?{' '}
                        <a
                            href={
                                window?.FEAS_ENV?.REACT_APP_GET_HELP_LINK ||
                                'https://support.severalnines.com/hc/en-us'
                            }
                            download
                            target="_blank"
                            rel="noreferrer noopener"
                            style={
                                currentApp !== APP_CCX
                                    ? {
                                          color: '#80ca90',
                                      }
                                    : {
                                          color: '#160482',
                                      }
                            }
                        >
                            Get help
                        </a>
                    </p>
                ) : (
                    renderSent()
                )}
            </SinglePageForm.Content>
        </SinglePageForm>
    );
}
